// Simplified bootstrap placeholder until Angular CLI tooling installed.
import { AppComponent } from './app/app.component';
import { CalendarMonthComponent } from './app/calendar-month/calendar-month.component';
import { MonthDataService, DaySummary as MonthDaySummary } from './app/services/month-data.service';
import { DaySummaryService, DaySummary } from './app/services/day-detail.service';
import { sentimentClass, sentimentGradient, formatTriple } from './app/utils/color-util'; // R2 reuse
import { hrefForMonth, parseViewDay, parseViewMonth, type YearMonth } from './app/utils/month-nav';
import { monthSoFarHighlights, resolveMonthHighlights } from './app/utils/month-highlights';
import { UNTITLED_POST_LABEL, buildDayStoryBoard, listDayPosts } from './app/utils/day-stories';
import type { MonthExamplePost, MonthHighlightItem } from './app/utils/month-highlights';
import { logger } from './logging/logger'; // G16

export interface CalendarRuntime {
  document: Document;
  location: Location;
  history: History;
  monthDataService: MonthDataService;
  daySummaryService: DaySummaryService;
  now?: () => Date;
}

export const MOBILE_CALENDAR_MQ = '(max-width: 40rem)';

let activeDayDate: string | null = null;
let overlayMode: 'day' | 'topic' | null = null;
let loadGeneration = 0;
let calendar: CalendarMonthComponent | null = null;
let lastFocusedCell: HTMLElement | null = null;
const monthTopicExamples = new Map<string, { topic: string; posts: MonthExamplePost[] }>();

export function mobileCalendarNewestFirst(win?: Window | null): boolean {
  return win?.matchMedia?.(MOBILE_CALENDAR_MQ).matches === true;
}

export function daysForCalendarGrid<T extends { date: string }>(days: T[], newestFirst: boolean): T[] {
  const sorted = [...days].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  return newestFirst ? sorted.reverse() : sorted;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatStoryItem(
  item: { topic: string; name?: string },
  topicClass: string,
  nameClass: string,
  topicKey?: string,
): string {
  const topicText = `<span class="${topicClass}">${escapeHtml(item.topic)}</span>`;
  const topic = topicKey
    ? `<div><span class="hl-label">Topic</span> <button type="button" class="month-topic-open" data-topic-key="${escapeHtml(topicKey)}">${topicText}</button></div>`
    : `<div><span class="hl-label">Topic</span> ${topicText}</div>`;
  if (!item.name) return `<li>${topic}</li>`;
  return `<li>${topic}<div><span class="hl-label">Name</span> <span class="${nameClass}">${escapeHtml(item.name)}</span></div></li>`;
}

function formatPostTime(iso?: string, includeDate = false): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return includeDate ? iso.slice(0, 16).replace('T', ' ') : iso.slice(11, 16);
  }
  const time = d.toISOString().slice(11, 16) + ' UTC';
  return includeDate ? `${d.toISOString().slice(0, 10)} · ${time}` : time;
}

function formatDayPost(post: {
  url: string;
  title: string;
  snippet: string;
  fullText?: string;
  imageDescription?: string;
  imageUrls?: string[];
  expandable?: boolean;
  publishedAt?: string;
  sentiment?: string;
  selfPraise?: boolean;
}, options: { includeDate?: boolean } = {}): string {
  const hasFullText = Boolean(post.fullText && post.fullText !== post.title);
  const photos = (post.imageUrls || []).map((url) => {
    const alt = escapeHtml(post.imageDescription || 'Post image');
    return `<img class="day-post-photo" src="${escapeHtml(url)}" alt="${alt}" loading="lazy" referrerpolicy="no-referrer">`;
  }).join('');
  const media = photos ? `<div class="day-post-media">${photos}</div>` : '';
  const hideUntitled = Boolean(photos && post.title === UNTITLED_POST_LABEL);
  const classes = ['day-post'];
  if (post.expandable) classes.push('is-expandable');
  if (hasFullText) classes.push('has-fulltext');
  if (post.selfPraise) classes.push('is-self-praise');
  const badge = post.selfPraise ? `<span class="day-post-self-praise">Self-praise</span>` : '';
  const title = hideUntitled ? '' : `<p class="day-post-title">${escapeHtml(post.title)}</p>`;
  const fullText = hasFullText
    ? `<p class="day-post-fulltext">${escapeHtml(post.fullText!)}</p>`
    : '';
  const snippet = !post.expandable && post.snippet
    ? `<span class="day-post-snippet">${escapeHtml(post.snippet)}</span>`
    : '';
  const image = post.imageDescription
    ? `<div class="day-post-image"><span class="day-post-image-label">Image description</span><p class="day-post-image-text">${escapeHtml(post.imageDescription)}</p></div>`
    : '';
  const meta = [formatPostTime(post.publishedAt, options.includeDate), post.sentiment].filter(Boolean).join(' · ');
  const toggle = post.expandable
    ? `<button type="button" class="day-post-toggle" aria-expanded="false">Show more</button>`
    : '';
  const original = post.url
    ? `<a class="day-post-original" href="${escapeHtml(post.url)}" target="_blank" rel="noopener noreferrer">Original post</a>`
    : '';
  return `<li class="${classes.join(' ')}">
    <div class="day-post-main">
      ${badge}
      ${title}
      ${fullText}
      ${snippet}
      ${media}
      ${image}
    </div>
    <div class="day-post-footer">
      <span class="day-post-meta">${escapeHtml(meta)}</span>
      <div class="day-post-actions">${toggle}${original}</div>
    </div>
  </li>`;
}

function ensureDayDetail(runtime: CalendarRuntime): HTMLElement {
  const doc = runtime.document;
  let root = doc.getElementById('day-detail');
  if (root) return root;

  root = doc.createElement('div');
  root.id = 'day-detail';
  root.hidden = true;
  root.innerHTML = `
    <div class="day-detail-backdrop" data-day-detail-close="true"></div>
    <article class="day-detail-panel" role="dialog" aria-modal="true" aria-labelledby="day-detail-title">
      <header class="day-detail-header">
        <h2 id="day-detail-title">Day</h2>
        <button type="button" id="day-detail-close" aria-label="Close day view">Close</button>
      </header>
      <div id="day-detail-body" class="day-detail-body"></div>
    </article>
  `;
  doc.body.appendChild(root);
  root.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (target?.getAttribute('data-day-detail-close') === 'true' || target?.id === 'day-detail-close') {
      closeDayDetail(runtime, 'push');
      return;
    }
    const toggle = target?.closest?.('.day-post-toggle') as HTMLButtonElement | null;
    if (!toggle || !root.contains(toggle)) return;
    const item = toggle.closest('.day-post');
    if (!item) return;
    event.preventDefault();
    const expanded = item.classList.toggle('is-expanded');
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    toggle.textContent = expanded ? 'Show less' : 'Show more';
  });
  return root;
}

function currentView(runtime: CalendarRuntime): YearMonth {
  return parseViewMonth(runtime.location.search, (runtime.now ?? (() => new Date()))());
}

function writeViewUrl(runtime: CalendarRuntime, view: YearMonth, mode: 'push' | 'replace', day?: string) {
  const next = hrefForMonth(runtime.location.pathname, view, day);
  const url = `${next}${runtime.location.hash || ''}`;
  if (mode === 'push') {
    runtime.history.pushState({ ...view, day }, '', url);
  } else {
    runtime.history.replaceState({ ...view, day }, '', url);
  }
}

function setDayDetailOpen(runtime: CalendarRuntime, open: boolean) {
  runtime.document.body.classList.toggle('day-detail-open', open);
  runtime.document.documentElement.classList.toggle('day-detail-open', open);
}

function closeDayDetail(runtime: CalendarRuntime, mode: 'push' | 'replace' | 'none') {
  const root = runtime.document.getElementById('day-detail');
  if (root) {
    root.hidden = true;
    const body = runtime.document.getElementById('day-detail-body');
    if (body) body.innerHTML = '';
  }
  setDayDetailOpen(runtime, false);
  activeDayDate = null;
  overlayMode = null;
  if (mode !== 'none') {
    writeViewUrl(runtime, currentView(runtime), mode);
    lastFocusedCell?.focus();
  }
}

function renderDayDetail(runtime: CalendarRuntime, summary: DaySummary) {
  const root = ensureDayDetail(runtime);
  const title = runtime.document.getElementById('day-detail-title');
  const body = runtime.document.getElementById('day-detail-body');
  if (!title || !body) return;

  const board = buildDayStoryBoard({
    posts: summary.posts,
    sentiments: summary.postSentiments,
    selfPraisePostIds: summary.selfPraisePostIds,
    trendingTopic: summary.trendingTopic,
    trendingName: summary.trendingName,
    secondaryHighlights: summary.secondaryHighlights,
  });
  const primaryTopic = board.primary.topic || summary.trendingTopic || '—';
  const primaryName = board.primary.name || summary.trendingName || '';
  const secondaryRows = board.secondary.length
    ? board.secondary.map((s) => formatStoryItem(s, 'day-story-topic', 'day-story-name')).join('')
    : '<li class="day-empty">No additional stories</li>';
  const postRows = board.posts.length
    ? board.posts.map((p) => formatDayPost(p)).join('')
    : '<li class="day-empty">No posts for this day</li>';

  title.textContent = summary.date;
  body.innerHTML = `
    <section class="day-primary">
      <p class="hl-row"><span class="hl-label">Trending topic</span> <span id="day-trending-topic">${escapeHtml(primaryTopic)}</span></p>
      ${primaryName ? `<p class="hl-row"><span class="hl-label">Trending name</span> <span id="day-trending-name">${escapeHtml(primaryName)}</span></p>` : ''}
    </section>
    <section class="day-secondary">
      <h3>Also trending</h3>
      <ol id="day-secondary-list">${secondaryRows}</ol>
    </section>
    <section class="day-posts">
      <h3>Posts <span class="day-post-count">${board.posts.length}</span></h3>
      <ul id="day-posts-list">${postRows}</ul>
    </section>
  `;
  root.hidden = false;
  setDayDetailOpen(runtime, true);
  overlayMode = 'day';
  activeDayDate = summary.date;
  runtime.document.getElementById('day-detail-close')?.focus();
}

async function openDayDetail(runtime: CalendarRuntime, date: string, mode: 'push' | 'replace' | 'none') {
  const root = ensureDayDetail(runtime);
  const title = runtime.document.getElementById('day-detail-title');
  const body = runtime.document.getElementById('day-detail-body');
  if (title) title.textContent = date;
  if (body) body.innerHTML = '<p class="day-detail-loading">Loading day…</p>';
  root.hidden = false;
  setDayDetailOpen(runtime, true);
  overlayMode = 'day';
  activeDayDate = date;
  if (mode !== 'none') writeViewUrl(runtime, currentView(runtime), mode, date);
  try {
    const summary = await runtime.daySummaryService.getSummary(date);
    if (activeDayDate !== date) return;
    renderDayDetail(runtime, summary);
  } catch (e) {
    if (activeDayDate !== date) return;
    if (body) {
      body.innerHTML = `<p class="day-detail-error">Error: ${escapeHtml(e instanceof Error ? e.message : 'Unknown error')}</p>`;
    }
    logger.error('Failed to load day summary', { error: e instanceof Error ? e.message : String(e), date });
  }
}

function monthKey(view: YearMonth): string {
  return `${view.year}-${String(view.month).padStart(2, '0')}`;
}

function renderMonthTopicPosts(runtime: CalendarRuntime, topic: string, posts: MonthExamplePost[]) {
  const root = ensureDayDetail(runtime);
  const title = runtime.document.getElementById('day-detail-title');
  const body = runtime.document.getElementById('day-detail-body');
  if (!title || !body) return;
  const boardPosts = listDayPosts(posts);
  const postRows = boardPosts.length
    ? boardPosts.map((p) => formatDayPost(p, { includeDate: true })).join('')
    : '<li class="day-empty">No posts for this topic</li>';
  title.textContent = topic;
  body.innerHTML = `
    <section class="day-posts">
      <h3>Posts <span class="day-post-count">${boardPosts.length}</span></h3>
      <ul id="day-posts-list">${postRows}</ul>
    </section>
  `;
  root.hidden = false;
  setDayDetailOpen(runtime, true);
  overlayMode = 'topic';
  activeDayDate = null;
  runtime.document.getElementById('day-detail-close')?.focus();
}

function bindMonthHighlightClicks(runtime: CalendarRuntime) {
  const section = runtime.document.getElementById('month-highlights');
  if (!section || section.dataset.topicClicksBound === 'true') return;
  section.dataset.topicClicksBound = 'true';
  section.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    const button = target?.closest?.('.month-topic-open') as HTMLElement | null;
    if (!button || !section.contains(button)) return;
    const entry = monthTopicExamples.get(button.getAttribute('data-topic-key') || '');
    if (!entry?.posts.length) return;
    event.preventDefault();
    renderMonthTopicPosts(runtime, entry.topic, entry.posts);
  });
}

function renderMonthHighlights(runtime: CalendarRuntime, view: YearMonth, month: {
  days: Array<{
    date: string;
    trendingTopic?: string;
    trendingName?: string;
    postsCount?: number;
    secondaryHighlights?: MonthHighlightItem[];
  }>;
  trendingTopic?: string;
  trendingName?: string;
  examplePosts?: MonthExamplePost[];
  secondaryHighlights?: MonthHighlightItem[];
}) {
  const section = runtime.document.getElementById('month-highlights');
  const heading = runtime.document.getElementById('month-highlights-heading');
  const topicEl = runtime.document.getElementById('month-trending-topic');
  const nameEl = runtime.document.getElementById('month-trending-name');
  const secondaryWrap = runtime.document.getElementById('month-secondary');
  const secondaryList = runtime.document.getElementById('month-secondary-list');
  if (!section || !topicEl || !nameEl) return;
  bindMonthHighlightClicks(runtime);

  const days = month.days || [];
  const today = (runtime.now ?? (() => new Date()))().toISOString().slice(0, 10);
  const key = monthKey(view);
  const highlights = resolveMonthHighlights(
    {
      trendingTopic: month.trendingTopic,
      trendingName: month.trendingName,
      examplePosts: month.examplePosts,
      secondaryHighlights: month.secondaryHighlights
    },
    monthSoFarHighlights(days, key, today)
  );
  const isCurrent = today.startsWith(key);
  if (heading) heading.textContent = isCurrent ? 'Month so far' : 'This month';
  monthTopicExamples.clear();
  const trendingPosts = highlights.examplePosts || [];
  const topicRow = topicEl.closest('.hl-row') || topicEl.parentElement;
  if (topicRow instanceof HTMLElement) {
    const topicText = highlights.trendingTopic || '—';
    if (trendingPosts.length && highlights.trendingTopic) {
      monthTopicExamples.set('trending', { topic: highlights.trendingTopic, posts: trendingPosts });
      topicRow.innerHTML = `<span class="hl-label">Trending topic</span> <button type="button" class="month-topic-open" id="month-trending-topic" data-topic-key="trending">${escapeHtml(topicText)}</button>`;
    } else {
      topicRow.innerHTML = `<span class="hl-label">Trending topic</span> <span id="month-trending-topic">${escapeHtml(topicText)}</span>`;
    }
  }
  const liveNameEl = runtime.document.getElementById('month-trending-name');
  const nameRow = liveNameEl?.closest('.hl-row') || liveNameEl?.parentElement;
  if (liveNameEl) {
    if (highlights.trendingName) {
      liveNameEl.textContent = highlights.trendingName;
      if (nameRow instanceof HTMLElement) nameRow.hidden = false;
    } else {
      liveNameEl.textContent = '';
      if (nameRow instanceof HTMLElement) nameRow.hidden = true;
    }
  }
  const extras = highlights.secondaryHighlights || [];
  if (secondaryList) {
    secondaryList.innerHTML = extras.length
      ? extras.map((item, index) => {
          const topicKey = item.examplePosts?.length ? `secondary-${index}` : undefined;
          if (topicKey) monthTopicExamples.set(topicKey, { topic: item.topic, posts: item.examplePosts || [] });
          return formatStoryItem(item, 'month-story-topic', 'month-story-name', topicKey);
        }).join('')
      : '';
  }
  if (secondaryWrap) secondaryWrap.hidden = extras.length === 0;
  section.hidden = days.length === 0;
}

function syncNav(runtime: CalendarRuntime, view: YearMonth) {
  const label = runtime.document.getElementById('month-label');
  const prev = runtime.document.getElementById('prev-month') as HTMLButtonElement | null;
  const next = runtime.document.getElementById('next-month') as HTMLButtonElement | null;
  const grid = runtime.document.getElementById('grid');
  if (!calendar) {
    calendar = new CalendarMonthComponent(view.year, view.month, runtime.now);
  } else {
    calendar.setView(view);
  }
  if (label) label.textContent = calendar.monthTitle();
  if (grid) {
    grid.setAttribute('aria-label', calendar.ariaLabel());
    grid.setAttribute('aria-busy', calendar.ariaBusy());
  }
  if (prev) {
    prev.disabled = !calendar.canGoPrevious();
    prev.setAttribute('aria-disabled', prev.disabled ? 'true' : 'false');
  }
  if (next) {
    next.disabled = !calendar.canGoNext();
    next.setAttribute('aria-disabled', next.disabled ? 'true' : 'false');
  }
}

async function loadMonth(runtime: CalendarRuntime, view: YearMonth) { // R1 fetch month summaries
  const statusEl = runtime.document.getElementById('status');
  const grid = runtime.document.getElementById('grid');
  const emptyEl = runtime.document.getElementById('empty-state');
  if (!statusEl || !grid) return;

  const gen = ++loadGeneration;
  closeDayDetail(runtime, 'none');
  if (!calendar) {
    calendar = new CalendarMonthComponent(view.year, view.month, runtime.now);
  }
  calendar.setLoading(true);
  syncNav(runtime, view);

  try {
    statusEl.textContent = 'Loading month data...';
    if (emptyEl) emptyEl.hidden = true;
    grid.hidden = true;
    // R1,R5: Fetch static month JSON via service (G1-configurable)
    const month = await runtime.monthDataService.getMonth(view.year, view.month);
    if (gen !== loadGeneration) return;
    const days = month.days;
    calendar.setData(days);
    calendar.setLoading(false);
    syncNav(runtime, view);
    renderMonthHighlights(runtime, view, month);
    statusEl.textContent = '';
    grid.innerHTML = '';

    if (calendar.isEmpty()) {
      if (emptyEl) {
        emptyEl.textContent = calendar.emptyStateMessage();
        emptyEl.hidden = false;
      } else {
        statusEl.textContent = calendar.emptyStateMessage();
      }
      grid.hidden = true;
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    grid.hidden = false;
    renderDayCells(runtime, days);

    const requestedDay = parseViewDay(runtime.location.search);
    if (requestedDay && days.some((d: { date: string }) => d.date === requestedDay)) {
      void openDayDetail(runtime, requestedDay, 'none');
    }
  } catch (e) {
    if (gen !== loadGeneration) return;
    calendar.setLoading(false);
    syncNav(runtime, view);
    statusEl.textContent = `Error loading data: ${e instanceof Error ? e.message : 'Unknown error'}`;
    logger.error('Failed to load month data', { error: e instanceof Error ? e.message : String(e) });
    renderMonthHighlights(runtime, view, { days: [] });
  }
}

function renderDayCells(runtime: CalendarRuntime, days: MonthDaySummary[]) {
  const grid = runtime.document.getElementById('grid');
  if (!grid) return;
  grid.innerHTML = '';
  const newestFirst = mobileCalendarNewestFirst(runtime.document.defaultView);
  daysForCalendarGrid(days, newestFirst).forEach((d) => {
    const triple = formatTriple(d.sentiments); // R2
    const gradient = sentimentGradient(d.sentiments);
    const cls = gradient ? 'sentiment-blend' : sentimentClass(d.sentiments);
    const cell = runtime.document.createElement('div');
    cell.className = `day ${cls}`;
    if (gradient) cell.style.setProperty('--day-blend', gradient);
    cell.dataset.date = d.date; // R10 UTC date label
    cell.tabIndex = 0;
    cell.setAttribute('role', 'button');
    cell.setAttribute('aria-haspopup', 'dialog');
    const n = Number(d.selfPraiseCount) || 0;
    const praiseLabel = n === 1 ? 'self-praise' : 'self-praises';
    const topicPreview = runtime.document.createElement('div');
    topicPreview.className = 'topic-preview';
    topicPreview.textContent = d.trendingTopic || (d.summary ? `Summary: ${d.summary}` : 'Summary: unavailable');

    cell.innerHTML = `<div class="self-praise"><div class="self-praise-count">${n}</div><div class="self-praise-label">${praiseLabel}</div></div><strong>${d.date}</strong><div class="day-line">Posts: ${d.postsCount}</div><div class="day-line">${triple}</div>`;
    cell.appendChild(topicPreview);
    cell.setAttribute('aria-label', `Open details for ${d.date}, ${n} ${praiseLabel}`);

    const showDetail = () => {
      lastFocusedCell = cell;
      void openDayDetail(runtime, d.date, 'push');
    };

    cell.addEventListener('click', showDetail);
    cell.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        showDetail();
      }
    });

    grid.appendChild(cell);
  });
}

function watchMobileCalendarOrder(runtime: CalendarRuntime) {
  const mq = runtime.document.defaultView?.matchMedia?.(MOBILE_CALENDAR_MQ);
  if (!mq) return;
  const onChange = () => {
    if (calendar?.days?.length) renderDayCells(runtime, calendar.days);
  };
  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
  else (mq as MediaQueryList & { addListener?: (cb: () => void) => void }).addListener?.(onChange);
}

function goToMonth(runtime: CalendarRuntime, view: YearMonth, mode: 'push' | 'replace' | 'none') {
  if (mode !== 'none') writeViewUrl(runtime, view, mode);
  return loadMonth(runtime, view);
}

function defaultRuntime(): CalendarRuntime {
  return {
    document,
    location,
    history,
    monthDataService: new MonthDataService(), // R1,R5
    daySummaryService: new DaySummaryService(), // R1.1,R5.1
  };
}

export function initCalendar(runtime: CalendarRuntime = defaultRuntime()) {
  const now = runtime.now ?? (() => new Date());
  calendar = new CalendarMonthComponent(undefined, undefined, now);
  const initial = parseViewMonth(runtime.location.search, now());
  writeViewUrl(runtime, initial, 'replace', parseViewDay(runtime.location.search));

  const prev = runtime.document.getElementById('prev-month');
  const next = runtime.document.getElementById('next-month');
  prev?.addEventListener('click', () => {
    if (!calendar?.canGoPrevious()) return;
    void goToMonth(runtime, calendar.previousMonth(), 'push');
  });
  next?.addEventListener('click', () => {
    if (!calendar?.canGoNext()) return;
    void goToMonth(runtime, calendar.nextMonth(), 'push');
  });

  runtime.document.addEventListener('keydown', (event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    if (event.key === 'Escape' && overlayMode) {
      event.preventDefault();
      closeDayDetail(runtime, 'push');
      return;
    }
    if (overlayMode) return;
    if (event.key === 'ArrowLeft' && calendar?.canGoPrevious()) {
      event.preventDefault();
      void goToMonth(runtime, calendar.previousMonth(), 'push');
    }
    if (event.key === 'ArrowRight' && calendar?.canGoNext()) {
      event.preventDefault();
      void goToMonth(runtime, calendar.nextMonth(), 'push');
    }
  });

  runtime.document.defaultView?.addEventListener('popstate', () => {
    const view = parseViewMonth(runtime.location.search, now());
    void goToMonth(runtime, view, 'none');
  });
  watchMobileCalendarOrder(runtime);

  void goToMonth(runtime, initial, 'none');
  return calendar;
}

// Preserve previous export for tests expecting bootstrap symbol.
export function bootstrap() { return new AppComponent(); }

const isJest = typeof process !== 'undefined' && !!process.env.JEST_WORKER_ID;
if (!isJest && document.getElementById('grid')) {
  initCalendar();
}
